"use client";

import { useTranslations } from "@/i18n/client";
import type { BlokProductListingPage } from "@/storyblok";
import type { Category } from "@frend-digital/ecom-layer";
import { clsx } from "@frend-digital/ui";
import { useLayoutEffect, useRef, useState } from "react";
import type { ISbStoryData } from "storyblok-js-client";
import styles from "./ProductListingPage.module.css";

const MAX_LINES = 2;
const ELLIPSIS = "… ";

/**
 * Finds the largest number of whole words that fits within MAX_LINES together with the
 * ellipsis and toggle label. Measured in an invisible clone so the visible text is never
 * cut mid-word.
 */
const fitWords = (measure: HTMLElement, words: string[], label: string) => {
	const lineHeight = Number.parseFloat(getComputedStyle(measure).lineHeight);
	const fits = (text: string) => {
		measure.textContent = text;
		return measure.getBoundingClientRect().height <= lineHeight * MAX_LINES + 1;
	};

	if (fits(words.join(" "))) return words.length;

	let low = 0;
	let high = words.length;
	while (low < high) {
		const mid = Math.ceil((low + high) / 2);
		if (fits(words.slice(0, mid).join(" ") + ELLIPSIS + label)) {
			low = mid;
		} else {
			high = mid - 1;
		}
	}
	return low;
};

export const Description = ({
	story,
	category,
}: { story?: ISbStoryData<BlokProductListingPage>; category?: Category }) => {
	const t = useTranslations();
	const [isExpanded, setIsExpanded] = useState(false);
	/** Number of words to show when collapsed. `null` means the text fits or isn't measured yet */
	const [visibleWords, setVisibleWords] = useState<number | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const measureRef = useRef<HTMLParagraphElement>(null);
	const contentRef = useRef<HTMLParagraphElement>(null);
	/** Height of the text right before a toggle, used as the start of the transition */
	const previousHeight = useRef<number | null>(null);

	const description = story?.content?.description;
	const title = story?.content?.title || category?.name || "";
	const readMore = t("Read more");
	const showLess = t("Show less");

	useLayoutEffect(() => {
		const container = containerRef.current;
		const measure = measureRef.current;
		if (!description || !container || !measure) return;

		const words = description.trim().split(/\s+/);

		const update = () => {
			const count = fitWords(measure, words, readMore);
			measure.textContent = "";
			setVisibleWords(count < words.length ? count : null);
		};

		update();
		const observer = new ResizeObserver(update);
		observer.observe(container);
		return () => observer.disconnect();
	}, [description, readMore]);

	const toggleExpanded = () => {
		previousHeight.current = contentRef.current?.offsetHeight ?? null;
		setIsExpanded((expanded) => !expanded);
	};

	// Animate the container height between the collapsed and expanded text
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs after each toggle
	useLayoutEffect(() => {
		const container = containerRef.current;
		const content = contentRef.current;
		const from = previousHeight.current;
		previousHeight.current = null;
		if (!container || !content || from === null) return;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

		const to = content.offsetHeight;
		if (from === to) return;

		container.style.overflow = "hidden";
		const animation = container.animate([{ height: `${from}px` }, { height: `${to}px` }], {
			duration: 400,
			easing: "cubic-bezier(0.16, 1, 0.3, 1)",
		});
		const reset = () => {
			container.style.overflow = "";
		};
		animation.onfinish = reset;
		animation.oncancel = reset;
		return () => animation.cancel();
	}, [isExpanded]);

	const heading = (
		<h1 className={styles.title} data-plp-title>
			{title}
		</h1>
	);

	if (!description) return heading;

	const words = description.trim().split(/\s+/);
	const isTruncated = visibleWords !== null;

	return (
		<>
			{heading}
			<div ref={containerRef} className={styles.descriptionContainer}>
				<p
					ref={contentRef}
					className={clsx(
						styles.description,
						// Before measuring (SSR / first paint) fall back to a CSS clamp
						visibleWords === null && !isExpanded && styles.truncatedText,
					)}
				>
					{isExpanded || !isTruncated
						? description
						: `${words.slice(0, visibleWords).join(" ")}${ELLIPSIS}`}
					{isTruncated && (
						<>
							{isExpanded && " "}
							<button
								type="button"
								className={styles.toggle}
								aria-expanded={isExpanded}
								onClick={toggleExpanded}
							>
								{isExpanded ? showLess : readMore}
							</button>
						</>
					)}
				</p>
				<p ref={measureRef} aria-hidden className={clsx(styles.description, styles.measure)} />
			</div>
		</>
	);
};
